import {sleep} from '@benchling/js-utils';

const MAX_NUM_ATTEMPTS = 5;

export interface RequestWithRetriesOptions {
  maxTimeoutIncrementMs?: number;
  onMaxAttemptsExceeded?: () => void;
}

/**
 * Return DAO call with retries until it succeeds (max 5 attempts).
 * @param  {Function} requestFn function that makes a DAO call and returns a promise
 * @param  {Object}   options   object that specifies configuration options for the retry attempts
 * @return {Promise}            Q promise that resolves with the requestFn response
 */
export default async function requestWithRetries<T>(
  requestFn: () => PromiseLike<T> | T,
  options: RequestWithRetriesOptions = {}
): Promise<T> {
  const maxTimeoutIncrementMs = options.maxTimeoutIncrementMs != null ? options.maxTimeoutIncrementMs : 10000;

  let numAttempts = 0;
  let timeoutMs = 0;

  while (true) {
    numAttempts++;
    // Linearly increase timeout by a random amount in [0, 10) s.
    // Random so that different clients don't retry in lockstep.
    timeoutMs += Math.floor(Math.random() * maxTimeoutIncrementMs);

    try {
      return await Promise.resolve(requestFn());
    } catch (e) {
      const shouldRetry = e.retry || (e.responseJSON && e.responseJSON.retry);
      const hasMoreAttempts = numAttempts < MAX_NUM_ATTEMPTS;
      if (shouldRetry && hasMoreAttempts) {
        await sleep(timeoutMs);
      } else {
        if (options.onMaxAttemptsExceeded && !hasMoreAttempts && shouldRetry) {
          options.onMaxAttemptsExceeded();
        }
        throw e;
      }
    }
  }
}
